/**
 * The Toplu Üretim flow's own reference helpers — `m07-import.js`, plus the three upstream pieces
 * it leans on: the single validation engine (`m01c-validate.js`), the strict date engine
 * (`m01d-dates.js`) and the offline spreadsheet reader (`m06b-xlsx.js`).
 *
 * They live here rather than in `reference.ts` because that file is shared by every screen and is
 * not to be edited. Everything below is ported rule for rule, because the import screen's visible
 * output is almost entirely derived: which columns exist, which rows are invalid, what the error
 * sentence says and which rows the duplicate policy will skip.
 *
 * Two rules from the contract that the port must not soften: validation is fail-closed (rule 11) —
 * a row that cannot be read is never issued, it is reported — and a batch is produced only from a
 * template's PUBLISHED release (rules 3 and 4), never from its draft.
 */
import constants from '../data/demo-constants.json';
import type {
  DemoCredential,
  DemoDatabase,
  DemoFieldDefinition,
  DemoRelease,
  DemoTemplate,
} from '../data/types';
import {
  addMonths,
  credentialViewState,
  currentRelease,
  displayName,
  fieldLabel,
  fmtDate,
  STATUS_TR,
  todayIso,
  trLower,
  trUpper,
} from './reference';
import {
  canonicalJSON,
  canvasH,
  canvasW,
  holderFieldKey,
  type Layout,
  type RenderData,
  resolveRenderPlan,
} from './render';

// ------------------------------------------------------------------ small utilities (`m01-utils`)

/** `shortCode` — the first three groups and an ellipsis, never the whole code in a table cell. */
export const shortCode = (code: string | null | undefined): string =>
  `${String(code ?? '')
    .split('-')
    .slice(0, 3)
    .join('-')}-…`;

export const slugify = (s: string): string =>
  trLower(s)
    .replace(/ı/g, 'i')
    .replace(/ş/g, 's')
    .replace(/ç/g, 'c')
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/ğ/g, 'g')
    .replace(/[^a-z0-9]+/g, '.')
    .replace(/^\.+|\.+$/g, '');

/** The BOM is what makes Excel open a UTF-8 CSV as UTF-8, so upstream always prepends it. */
export function downloadText(filename: string, text: string, mime = 'text/plain'): void {
  const a = document.createElement('a');
  a.href = `data:${mime};charset=utf-8,${encodeURIComponent(`﻿${text}`)}`;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

let uidCounter = 2000;
export const uid = (prefix = 'id'): string => `${prefix}_${(++uidCounter).toString(36)}`;

const CROCK32 = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/** `generateUniquePublicCode` — HPW- plus 20 Crockford characters in groups of four (rule 6). */
export function generateUniquePublicCode(db: DemoDatabase): string {
  for (let attempt = 0; attempt < 20; attempt++) {
    const bytes = new Uint8Array(20);
    crypto.getRandomValues(bytes);
    let raw = '';
    for (const b of bytes) raw += CROCK32[b % 32];
    const code = `HPW-${(raw.match(/.{1,4}/g) ?? []).join('-')}`;
    if (!db.credentials.some((c) => c.publicCode === code)) return code;
  }
  throw new Error('Public kod üretilemedi');
}

export async function sha256Canonical(value: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(canonicalJSON(value));
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

// ------------------------------------------------------------- strict dates (`m01d-dates.js` §3)

const DATE_ERROR_TR: Record<string, string> = {
  empty: 'Tarih boş.',
  format: 'Tarih biçimi tanınmadı. YYYY-AA-GG ya da GG.AA.YYYY kullanın.',
  not_a_calendar_date: 'Bu takvimde olmayan bir tarih (gün/ay geçersiz).',
  month_range: 'Ay 1–12 aralığında olmalı.',
  day_range: 'Gün, seçilen ay için geçerli aralıkta olmalı.',
  year_range: 'Yıl 1900–2200 aralığında olmalı.',
};

const MIN_YEAR = 1900;
const MAX_YEAR = 2200;
const pad2 = (n: number): string => String(n).padStart(2, '0');
const isLeapYear = (y: number): boolean => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
const daysInMonth = (y: number, m: number): number =>
  m >= 1 && m <= 12
    ? ([31, isLeapYear(y) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1] as number)
    : 0;

export interface DateResult {
  ok: boolean;
  value?: string;
  code?: string;
  message?: string;
}

const dErr = (code: string): DateResult => ({
  ok: false,
  code,
  message: DATE_ERROR_TR[code] ?? (DATE_ERROR_TR.format as string),
});

/**
 * `parseDateOnlyStrict` — the component round-trip is the point: `new Date(2026, 1, 31)` silently
 * becomes 3 March, so a calendar-impossible date has to be rejected before it ever reaches a
 * record.
 */
export function parseDateOnlyStrict(value: unknown): DateResult {
  if (value === null || value === undefined || String(value).trim() === '') return dErr('empty');
  let s = String(value).trim();
  const isoT = /^(\d{4})-(\d{2})-(\d{2})[T ]/.exec(s);
  if (isoT) s = `${isoT[1]}-${isoT[2]}-${isoT[3]}`;
  let y: number;
  let m: number;
  let d: number;
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (iso) {
    y = Number(iso[1]);
    m = Number(iso[2]);
    d = Number(iso[3]);
  } else {
    /* TR biçimi: 31.12.2026 · 31/12/2026 · 31-12-2026 */
    const tr = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/.exec(s);
    if (!tr) return dErr('format');
    d = Number(tr[1]);
    m = Number(tr[2]);
    y = Number(tr[3]);
  }
  if (!(y >= MIN_YEAR && y <= MAX_YEAR)) return dErr('year_range');
  if (!(m >= 1 && m <= 12)) return dErr('month_range');
  const dim = daysInMonth(y, m);
  if (!(d >= 1 && d <= dim)) return dErr(d >= 1 && d <= 31 ? 'not_a_calendar_date' : 'day_range');
  const probe = new Date(Date.UTC(y, m - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() + 1 !== m || probe.getUTCDate() !== d)
    return dErr('not_a_calendar_date');
  return { ok: true, value: `${y}-${pad2(m)}-${pad2(d)}` };
}

/** The legacy contract upstream keeps: a value that cannot be read comes back as `'invalid'`. */
export function normalizeDate(v: unknown): string | null {
  if (v === null || v === undefined || String(v).trim() === '') return null;
  const r = parseDateOnlyStrict(v);
  return r.ok ? (r.value as string) : 'invalid';
}

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// ------------------------------------------------------ the single validation engine (`m01c`)

interface CatalogField {
  key: string;
  tr: string;
  en: string;
  dataType: string;
  visibility: string;
  sample?: string;
  auto?: boolean;
}

const FIELD_CATALOG = constants.FIELD_CATALOG as CatalogField[];
export const catalogField = (k: string): CatalogField | null =>
  FIELD_CATALOG.find((f) => f.key === k) ?? null;

const REMOVED_DYNAMIC_DATA_TYPES = ['image', 'signature'];
export const isGraphicDataType = (t: string | undefined): boolean =>
  REMOVED_DYNAMIC_DATA_TYPES.includes(String(t)) || t === 'qr';

const canRender = (v: string | undefined): boolean =>
  v === 'public_render' || v === 'public_metadata_and_render';

const NORMALIZERS: Record<string, (v: unknown) => string> = {
  none: (v) => String(v ?? ''),
  trim: (v) => String(v ?? '').trim(),
  collapse_spaces: (v) =>
    String(v ?? '')
      .replace(/\s+/g, ' ')
      .trim(),
  upper_tr: (v) => trUpper(String(v ?? '').trim()),
  lower_tr: (v) => trLower(String(v ?? '').trim()),
  title_tr: (v) =>
    String(v ?? '')
      .trim()
      .split(/\s+/)
      .map((w) => (w ? trUpper(w.slice(0, 1)) + trLower(w.slice(1)) : w))
      .join(' '),
};

function normalizeValue(def: DemoFieldDefinition | null, raw: unknown): string {
  const fns = ['collapse_spaces', ...(def?.normalization ?? [])];
  let v = String(raw ?? '');
  for (const n of fns) {
    const f = NORMALIZERS[n];
    if (f) v = f(v);
  }
  return v;
}

export interface ClaimError {
  key?: string;
  code: string;
  message: string;
}

const fieldLabelOf = (f: DemoFieldDefinition): string =>
  (f.labels ? f.labels.tr || f.labels.en : null) || f.key;

/**
 * `validateFieldValue` — the reduced port: the data types the v0.7 seed's releases actually use
 * (string, text, enum, number, integer, date, email, boolean), with upstream's own messages. A
 * type outside that set is left as its normalized string rather than passed silently, which is the
 * same fail-closed direction upstream takes.
 */
function validateFieldValue(def: DemoFieldDefinition): (raw: unknown) => {
  ok: boolean;
  value: string;
  errors: ClaimError[];
} {
  return (raw: unknown) => {
    const errors: ClaimError[] = [];
    const label = fieldLabelOf(def);
    const V = (def.validation ?? {}) as {
      min?: number;
      max?: number;
      minLength?: number;
      maxLength?: number;
      enum?: unknown[];
      dateMin?: string;
      dateMax?: string;
    };
    let value = normalizeValue(def, raw);
    let empty = value === '';
    const fallback = def.defaultValue;
    if (empty && fallback !== undefined && fallback !== null && String(fallback) !== '') {
      /* §1.5 — a default value does NOT skip the pipeline; it is normalized and re-checked. */
      value = normalizeValue(def, fallback);
      empty = value === '';
    }
    if (empty) {
      if (def.required) errors.push({ code: 'required', message: `${label} zorunlu` });
      return { ok: errors.length === 0, value: '', errors };
    }
    const t = def.dataType || 'string';
    const str = value;
    if (t === 'email') {
      if (!EMAIL_RE.test(str)) errors.push({ code: 'email', message: `${label} geçerli bir e-posta değil` });
    } else if (t === 'number' || t === 'integer') {
      const n = Number(str.replace(',', '.'));
      if (!Number.isFinite(n)) errors.push({ code: 'number', message: `${label} sayı değil` });
      else {
        if (t === 'integer' && !Number.isInteger(n))
          errors.push({ code: 'integer', message: `${label} tam sayı olmalı` });
        if (V.min != null && n < V.min)
          errors.push({ code: 'min', message: `${label} en az ${V.min} olmalı` });
        if (V.max != null && n > V.max)
          errors.push({ code: 'max', message: `${label} en çok ${V.max} olabilir` });
        value = String(n);
      }
    } else if (t === 'date') {
      const r = parseDateOnlyStrict(str);
      if (!r.ok) errors.push({ code: r.code as string, message: `${label}: ${r.message}` });
      else {
        const dMin = V.dateMin ?? (V.min as unknown as string | undefined);
        const dMax = V.dateMax ?? (V.max as unknown as string | undefined);
        const v = r.value as string;
        if (dMin && v < dMin)
          errors.push({ code: 'dateMin', message: `${label} ${dMin} tarihinden önce olamaz` });
        if (dMax && v > dMax)
          errors.push({ code: 'dateMax', message: `${label} ${dMax} tarihinden sonra olamaz` });
        value = v;
      }
    } else if (t === 'boolean') {
      const b = /^(1|true|evet|yes|e|y|x)$/i.test(str)
        ? 'true'
        : /^(0|false|hayır|hayir|no|h|n)$/i.test(str)
          ? 'false'
          : null;
      if (b === null) errors.push({ code: 'boolean', message: `${label} evet/hayır olarak okunamadı` });
      else value = b;
    } else if (t === 'enum') {
      const opts = (V.enum ?? []).map((x) => String(x));
      if (!opts.length)
        errors.push({ code: 'enumEmpty', message: `${label} için seçenek listesi tanımlı değil` });
      else {
        const hit = opts.find((o) => trLower(o) === trLower(str));
        if (!hit)
          errors.push({
            code: 'enum',
            message: `${label} şu seçeneklerden biri olmalı: ${opts.join(', ')}`,
          });
        else value = hit;
      }
    }
    if (!errors.length && (t === 'string' || t === 'text' || t === 'url' || t === 'enum')) {
      if (V.minLength != null && str.length < V.minLength)
        errors.push({
          code: 'minLength',
          message: `${label} en az ${V.minLength} karakter olmalı`,
        });
      if (V.maxLength != null && str.length > V.maxLength)
        errors.push({
          code: 'maxLength',
          message: `${label} en çok ${V.maxLength} karakter olabilir`,
        });
    }
    return { ok: errors.length === 0, value, errors };
  };
}

export interface ClaimValidation {
  ok: boolean;
  values: Record<string, string>;
  errors: ClaimError[];
}

/** `validateClaims` — `auto` fields are the system's, and a graphic field is rejected, not skipped. */
export function validateClaims(
  fieldDefs: DemoFieldDefinition[],
  values: Record<string, string>,
): ClaimValidation {
  const out: ClaimValidation = { ok: true, values: {}, errors: [] };
  for (const f of (fieldDefs ?? []).filter((x) => !x.auto)) {
    if (isGraphicDataType(f.dataType)) {
      out.ok = false;
      out.errors.push({
        key: f.key,
        code: 'graphic_claim_unsupported',
        message: `«${fieldLabelOf(f)}» alanı görsel/imza türünde; dinamik görsel claim v1 kapsamında desteklenmiyor. Bunu Asset Layer olarak ekleyin.`,
      });
      continue;
    }
    const r = validateFieldValue(f)(values[f.key] ?? '');
    out.values[f.key] = r.value;
    if (!r.ok) {
      out.ok = false;
      for (const e of r.errors) out.errors.push({ key: f.key, ...e });
    }
  }
  return out;
}

// ------------------------------------------------------------------ duplicate key (`m03-state.js`)

export function duplicateKey(
  release: DemoRelease,
  claims: Record<string, string>,
  email: string | null | undefined,
): string {
  const pol = release.duplicatePolicy ?? { keyFields: [release.holderField], scope: 'template' };
  const parts = (pol.keyFields ?? []).map((k) =>
    k === 'email'
      ? trLower(String(email ?? '')).trim()
      : trLower(String(claims[k] ?? ''))
          .replace(/\s+/g, ' ')
          .trim(),
  );
  return `${pol.scope === 'global' ? 'global' : release.templateId}|${parts.join('|')}`;
}

/**
 * `findDuplicate` — §9's distinction: only a blocking state stops production, and an expired
 * record is a renewal rather than a duplicate when the policy allows it.
 */
export function findDuplicate(
  db: DemoDatabase,
  release: DemoRelease,
  claims: Record<string, string>,
  email: string | null | undefined,
): DemoCredential | null {
  const pol = release.duplicatePolicy ?? {
    keyFields: [release.holderField],
    scope: 'template',
    blockingStates: ['pending_acceptance', 'pending_admin_publish', 'active'],
    allowRenewalAfterExpiry: true,
    onDuplicate: 'skip' as const,
  };
  const blocking = pol.blockingStates ?? ['pending_acceptance', 'pending_admin_publish', 'active'];
  const renewalAllowed = pol.allowRenewalAfterExpiry !== false;
  const key = duplicateKey(release, claims, email);
  for (const c of db.credentials) {
    if (pol.scope !== 'global' && c.templateId !== release.templateId) continue;
    if (duplicateKey(c.release, c.claims as Record<string, string>, c.privateContactEmail) !== key) continue;
    const state = credentialViewState(c);
    if (state === 'expired' && renewalAllowed) continue;
    if (blocking.includes(state)) return c;
  }
  return null;
}

// -------------------------------------------------------------- import fields (`m07-import.js`)

export interface ImportField {
  key: string;
  labelTr: string;
  kind: 'value' | 'meta';
  required: boolean;
  sample: string;
  def: DemoFieldDefinition | null;
}

export const META_IMPORT_FIELDS: ImportField[] = [
  {
    key: 'email',
    labelTr: 'E-posta',
    kind: 'meta',
    required: true,
    sample: 'ad@sirket.com.tr',
    def: null,
  },
  {
    key: 'issue_date',
    labelTr: 'Veriliş Tarihi',
    kind: 'meta',
    required: false,
    sample: '02.09.2026',
    def: null,
  },
  {
    key: 'expiry_date',
    labelTr: 'Geçerlilik Tarihi',
    kind: 'meta',
    required: false,
    sample: '02.09.2027',
    def: null,
  },
  { key: 'lang', labelTr: 'Dil (tr/en)', kind: 'meta', required: false, sample: 'tr', def: null },
];

/** Batch production reads the PUBLISHED release only; a draft is never a source (rule 3). */
export const importRelease = (t: DemoTemplate | undefined): DemoRelease | null =>
  t ? currentRelease(t) : null;

const layoutFor = (t: DemoTemplate): Layout => (importRelease(t) ?? t.draft) as unknown as Layout;

/**
 * The column list is not hard-coded anywhere: it is derived from the chosen template's own field
 * definitions, so a template that grows a field grows a column here without a code change.
 */
export function importFields(t: DemoTemplate): ImportField[] {
  const lay = layoutFor(t);
  const hf = holderFieldKey(lay);
  const defs = ((lay.fieldDefinitions ?? []) as unknown as DemoFieldDefinition[]).filter(
    (f) =>
      f.importable !== false &&
      (!f.auto || f.key === 'certification_period') &&
      !isGraphicDataType(f.dataType),
  );
  const own: ImportField[] = defs.map((f) => ({
    key: f.key,
    labelTr: fieldLabel(f, 'tr'),
    kind: 'value',
    required: !!f.required || f.key === hf,
    sample: f.sample || '',
    def: f,
  }));
  if (!own.some((f) => f.key === hf))
    own.unshift({
      key: hf,
      labelTr: catalogField(hf)?.tr ?? hf,
      kind: 'value',
      required: true,
      sample: '',
      def: null,
    });
  return own.concat(META_IMPORT_FIELDS.filter((m) => !own.some((f) => f.key === m.key)));
}

export function importFieldLabel(t: DemoTemplate, key: string): string {
  return importFields(t).find((x) => x.key === key)?.labelTr ?? key;
}

export const requiredFields = (t: DemoTemplate): string[] =>
  importFields(t)
    .filter((f) => f.required)
    .map((f) => f.key);

export function relValidityMonths(t: DemoTemplate): number | null {
  const r = importRelease(t) ?? t.draft;
  return r.validityPolicy ? (r.validityPolicy.months ?? null) : null;
}

// --------------------------------------------------------------------- header matching (§3)

const normHeader = (h: unknown): string =>
  trLower(String(h))
    .replace(/ı/g, 'i')
    .replace(/ş/g, 's')
    .replace(/ç/g, 'c')
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/ğ/g, 'g')
    .replace(/[^a-z0-9]/g, '');

const HEADER_HINTS: [RegExp, string][] = [
  [/^(dil|lang|language)$/, 'lang'],
  [/(eposta|email|mail)/, 'email'],
  [/(sirket|company|organization|organisation|kurum|firma)/, 'organization_name'],
  [/(adsoyad|fullname|isim|^name$|^ad$|alici|recipient|katilimci|calisan)/, 'recipient_name'],
  [/(donem|period)/, 'certification_period'],
  [/(baslangic|verilis|issue|^tarih$|^date$)/, 'issue_date'],
  [/(bitis|gecerlilik|expiry|expiration|sonlanma|^son)/, 'expiry_date'],
  [/(program|egitim|course)/, 'program_name'],
  [/(unvan|position|title|gorev)/, 'position'],
  [/(departman|department|birim)/, 'department'],
  [/(kurul|committee|komite)/, 'committee_name'],
  [/(seviye|level|derece)/, 'award_level'],
  [/(puan|score|skor|not)/, 'score'],
  [/(kategori|category)/, 'category'],
  [/(egitmen|instructor|trainer)/, 'instructor'],
  [/(^yil$|year)/, 'period_year'],
];

export function suggestField(header: string, fields: ImportField[]): string {
  const h = normHeader(header);
  const exact = fields.find((f) => normHeader(f.key) === h || normHeader(f.labelTr) === h);
  if (exact) return exact.key;
  for (const [re, key] of HEADER_HINTS) if (re.test(h) && fields.some((f) => f.key === key)) return key;
  const partial = fields.find(
    (f) => h && (normHeader(f.labelTr).includes(h) || h.includes(normHeader(f.labelTr))),
  );
  return partial ? partial.key : '';
}

// ------------------------------------------------------------------- sample and mock spreadsheets

export function sampleCSV(t: DemoTemplate): string {
  const flds = importFields(t);
  const heads = flds.map((f) => f.labelTr);
  const row = (i: number): string[] =>
    flds.map((f) =>
      f.key === 'lang'
        ? i
          ? 'en'
          : 'tr'
        : f.key === 'email'
          ? i
            ? 'john.carter@example.com'
            : 'ayse.yilmaz@sirket.com.tr'
          : f.key === 'issue_date'
            ? '02.09.2026'
            : f.key === 'expiry_date'
              ? relValidityMonths(t)
                ? '02.09.2027'
                : ''
              : f.sample || (i ? 'Örnek 2' : 'Örnek 1'),
    );
  return [heads, row(0), row(1)]
    .map((r) => r.map((v) => (/[;"\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v)).join(';'))
    .join('\n');
}

export interface Sheet {
  name: string;
  headers: string[];
  rows: string[][];
}

/**
 * `mockRows` — the reference's «Örnek dosyayla devam et» list. The seeded LCG and the four
 * deliberate defects (a repeated name, a broken address, an empty holder, a name too long to fit)
 * are what make the validation step show something worth reviewing, so they are ported as they are.
 */
export function mockRows(t: DemoTemplate): { headers: string[]; rows: string[][] } {
  const rel = layoutFor(t);
  const flds = importFields(t);
  const hf = holderFieldKey(rel);
  const isOrg = rel.subjectType === 'organization';
  const first = [
    'Ayşe',
    'Mehmet',
    'Elif',
    'Can',
    'Zeynep',
    'Burak',
    'Selin',
    'Emre',
    'Deniz',
    'Ece',
    'Hakan',
    'Merve',
    'Gizem',
    'Kerem',
    'Fatma Nur',
    'Ozan',
    'İrem',
    'Yusuf',
    'Işıl',
    'Tolga',
  ];
  const last = [
    'Yılmaz',
    'Kaya',
    'Demir',
    'Öztürk',
    'Arslan',
    'Şahin',
    'Çelik',
    'Aydın',
    'Yıldız',
    'Koç',
    'Doğan',
    'Aksoy',
    'Kılıç',
    'Güneş',
    'Özdemir',
  ];
  const orgs = [
    'ABC Holding',
    'XYZ Teknoloji',
    'Kolektif Alan',
    'Lila Kağıt',
    'Dinamik Otomotiv',
    'Şerbet Gıda',
  ];
  let seed = 11;
  const rnd = (): number => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
  const pick = (a: string[]): string => a[Math.floor(rnd() * a.length)] as string;
  const suffix = ['Enerji', 'Perakende', 'Lojistik', 'Sağlık', 'Yazılım'];
  const n = isOrg ? 30 : 60;
  const rows: string[][] = [];
  const seen = new Set<string>();
  for (let i = 0; i < n; i++) {
    const person = `${pick(first)} ${pick(last)}`;
    const org = pick(orgs) + (isOrg ? ` ${suffix[i % 5]} ${i + 1}` : '');
    let email = isOrg
      ? `ik@${slugify(org).replace(/\./g, '')}.com.tr`
      : `${slugify(person)}@${slugify(org).replace(/\./g, '')}.com.tr`;
    while (seen.has(email)) email = email.replace('@', `${Math.floor(rnd() * 90 + 10)}@`);
    seen.add(email);
    rows.push(
      flds.map((f) =>
        f.key === 'lang'
          ? i % 17 === 0
            ? 'en'
            : 'tr'
          : f.key === 'email'
            ? email
            : f.key === 'issue_date'
              ? '02.09.2026'
              : f.key === 'expiry_date'
                ? relValidityMonths(t)
                  ? '02.09.2027'
                  : ''
                : f.key === hf
                  ? isOrg
                    ? org
                    : person
                  : f.key === 'organization_name'
                    ? org
                    : f.key === 'recipient_name'
                      ? person
                      : f.sample || '',
      ),
    );
  }
  const hi = flds.findIndex((f) => f.key === hf);
  const ei = flds.findIndex((f) => f.key === 'email');
  const at = (i: number): string[] | undefined => rows[i];
  const r4 = at(4);
  if (r4 && hi >= 0) r4[hi] = isOrg ? 'ABC Holding' : 'Ayşe Yılmaz';
  const r6 = at(6);
  if (r6 && ei >= 0) r6[ei] = 'bozuk.eposta@';
  const r9 = at(9);
  if (r9 && hi >= 0) r9[hi] = '';
  const r12 = at(12);
  if (r12 && hi >= 0)
    r12[hi] = isOrg
      ? 'Uluslararası Anadolu Lojistik ve Dağıtım Hizmetleri Anonim Şirketi'
      : 'Alexandra Konstantinopoulou-Wetherington';
  const r14 = at(14);
  if (at(15) && r14) rows[15] = r14.slice();
  return { headers: flds.map((f) => f.labelTr), rows };
}

export function parseCSV(text: string): { headers: string[]; rows: string[][] } {
  const src = text.replace(/^﻿/, '');
  const firstLine = src.split(/\r?\n/)[0] ?? '';
  const delim = (firstLine.match(/;/g) ?? []).length > (firstLine.match(/,/g) ?? []).length ? ';' : ',';
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i++;
        } else quoted = false;
      } else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === delim) {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else cell += ch;
  }
  if (cell !== '' || row.length) {
    row.push(cell);
    rows.push(row);
  }
  const nonEmpty = rows.filter((r) => r.some((c) => String(c).trim() !== ''));
  return { headers: (nonEmpty[0] ?? []).map((h) => String(h).trim()), rows: nonEmpty.slice(1) };
}

// ------------------------------------------------- offline XLSX reader (`m06b-xlsx.js`, §18A)

export const XLSX_LIMITS = {
  maxBytes: 15 * 1024 * 1024,
  maxUnzippedBytes: 120 * 1024 * 1024,
  maxCompressionRatio: 200,
  maxSheets: 25,
  maxRows: 20000,
  maxCols: 256,
  maxCells: 500000,
  maxCellChars: 4000,
  maxEntries: 500,
} as const;

/** §15 — honest scope: the old binary `.xls` is rejected rather than half-read. */
export const XLS_LEGACY_MESSAGE =
  "Eski ikili .xls biçimi desteklenmiyor. Excel'de «Farklı Kaydet → Excel Çalışma Kitabı (.xlsx)» ya da «CSV UTF-8 (.csv)» seçip yeniden yükleyin.";

const u16 = (b: Uint8Array, o: number): number =>
  new DataView(b.buffer, b.byteOffset, b.byteLength).getUint16(o, true);
const u32 = (b: Uint8Array, o: number): number =>
  new DataView(b.buffer, b.byteOffset, b.byteLength).getUint32(o, true);

export const xlsxSupported = (): boolean => typeof DecompressionStream === 'function';

async function inflateRaw(bytes: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([bytes as unknown as BlobPart])
    .stream()
    .pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** ZIP central directory → { name: bytes }, aborting early on a declared decompression bomb. */
async function unzip(buf: Uint8Array): Promise<Record<string, Uint8Array>> {
  const L = XLSX_LIMITS;
  let unzipped = 0;
  let eocd = -1;
  for (let i = buf.length - 22; i >= Math.max(0, buf.length - 66000); i--) {
    if (u32(buf, i) === 0x06054b50) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new Error('ZIP sonu (EOCD) bulunamadı — dosya bir XLSX değil ya da bozuk.');
  const count = u16(buf, eocd + 10);
  let off = u32(buf, eocd + 16);
  if (count > L.maxEntries) throw new Error(`ZIP içinde çok fazla girdi (${count} > ${L.maxEntries}).`);
  const deflated: Record<string, Uint8Array> = {};
  const stored: Record<string, Uint8Array> = {};
  for (let n = 0; n < count; n++) {
    if (u32(buf, off) !== 0x02014b50) break;
    const method = u16(buf, off + 10);
    const csize = u32(buf, off + 20);
    const usize = u32(buf, off + 24);
    const nameLen = u16(buf, off + 28);
    const extraLen = u16(buf, off + 30);
    const cmtLen = u16(buf, off + 32);
    const lho = u32(buf, off + 42);
    const name = new TextDecoder().decode(buf.subarray(off + 46, off + 46 + nameLen));
    unzipped += usize;
    if (unzipped > L.maxUnzippedBytes)
      throw new Error(
        `Açılmış içerik ${Math.round(L.maxUnzippedBytes / 1048576)} MB sınırını aşıyor (ZIP bombası koruması).`,
      );
    if (csize > 512 && usize / Math.max(1, csize) > L.maxCompressionRatio)
      throw new Error(
        `«${name}» için sıkıştırma oranı ${Math.round(usize / csize)}:1 — güvenli sınır ${L.maxCompressionRatio}:1 (ZIP bombası koruması).`,
      );
    const lNameLen = u16(buf, lho + 26);
    const lExtraLen = u16(buf, lho + 28);
    const dataStart = lho + 30 + lNameLen + lExtraLen;
    const raw = buf.subarray(dataStart, dataStart + csize);
    if (method === 0) stored[name] = raw.slice();
    else deflated[name] = raw;
    off += 46 + nameLen + extraLen + cmtLen;
  }
  const out: Record<string, Uint8Array> = { ...stored };
  let actual = Object.values(stored).reduce((a, b) => a + b.length, 0);
  for (const [name, bytes] of Object.entries(deflated)) {
    const inf = await inflateRaw(bytes);
    actual += inf.length;
    if (actual > L.maxUnzippedBytes)
      throw new Error(
        `Açılmış içerik ${Math.round(L.maxUnzippedBytes / 1048576)} MB sınırını aştı (ZIP bombası koruması).`,
      );
    out[name] = inf;
  }
  return out;
}

function rejectUnsafeParts(files: Record<string, Uint8Array>): void {
  const names = Object.keys(files);
  if (names.some((n) => /vbaProject\.bin$/i.test(n) || /^xl\/macrosheets\//i.test(n)))
    throw new Error('Dosya makro (VBA) içeriyor; makrolu çalışma kitapları kabul edilmez.');
  if (names.some((n) => /^xl\/externalLinks\//i.test(n)))
    throw new Error('Dosya dış çalışma kitabı bağlantıları içeriyor; kabul edilmez.');
  for (const n of names) {
    if (!/_rels\/.*\.rels$/i.test(n)) continue;
    const txt = new TextDecoder().decode(files[n] as Uint8Array);
    if (/TargetMode\s*=\s*"External"/i.test(txt) && /(oleObject|externalLink|hyperlink)/i.test(txt))
      throw new Error('Dosya dış kaynak ilişkileri (OLE / dış bağlantı) içeriyor; kabul edilmez.');
  }
}

const xml = (bytes: Uint8Array): Document =>
  new DOMParser().parseFromString(new TextDecoder().decode(bytes), 'application/xml');

const colIndex = (ref: string): number => {
  const m = /^([A-Z]+)/.exec(ref);
  if (!m) return 0;
  let n = 0;
  for (const ch of m[1] as string) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
};

const EXCEL_EPOCH = Date.UTC(1899, 11, 30);
const serialToISO = (n: number): string => {
  const d = new Date(EXCEL_EPOCH + Math.round(n) * 86400000);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
};

function dateStyles(files: Record<string, Uint8Array>): Set<number> {
  const set = new Set<number>();
  const st = files['xl/styles.xml'];
  if (!st) return set;
  const doc = xml(st);
  const fmts: Record<string, string> = {};
  for (const f of [...doc.getElementsByTagName('numFmt')])
    fmts[f.getAttribute('numFmtId') ?? ''] = f.getAttribute('formatCode') ?? '';
  const builtinDate = new Set(['14', '15', '16', '17', '18', '19', '20', '21', '22', '45', '46', '47']);
  const xfs = doc.getElementsByTagName('cellXfs')[0];
  if (!xfs) return set;
  [...xfs.getElementsByTagName('xf')].forEach((xf, i) => {
    const id = xf.getAttribute('numFmtId') ?? '0';
    const code = fmts[id];
    if (
      builtinDate.has(id) ||
      (code && /[dmy]/i.test(code.replace(/\[[^\]]*\]/g, '')) && !/^[#0.,%]+$/.test(code))
    )
      set.add(i);
  });
  return set;
}

async function readXlsx(file: File): Promise<{ sheets: Sheet[]; engine: string }> {
  const L = XLSX_LIMITS;
  if (!xlsxSupported())
    throw new Error('Bu tarayıcı DecompressionStream desteklemiyor; XLSX yerine CSV kullanın.');
  if (file.size > L.maxBytes)
    throw new Error(
      `Dosya ${Math.round(L.maxBytes / 1048576)} MB sınırını aşıyor (${(file.size / 1048576).toFixed(1)} MB).`,
    );
  const files = await unzip(new Uint8Array(await file.arrayBuffer()));
  rejectUnsafeParts(files);
  if (!files['xl/workbook.xml'])
    throw new Error('xl/workbook.xml yok — dosya XLSX değil (eski .xls ikili biçimi olabilir).');
  const shared: string[] = [];
  const ss = files['xl/sharedStrings.xml'];
  if (ss)
    for (const si of [...xml(ss).getElementsByTagName('si')])
      shared.push([...si.getElementsByTagName('t')].map((t) => t.textContent ?? '').join(''));
  const dstyles = dateStyles(files);
  const rels: Record<string, string> = {};
  const rf = files['xl/_rels/workbook.xml.rels'];
  if (rf)
    for (const r of [...xml(rf).getElementsByTagName('Relationship')])
      rels[r.getAttribute('Id') ?? ''] = (r.getAttribute('Target') ?? '')
        .replace(/^\/?xl\//, '')
        .replace(/^\//, '');
  const wb = xml(files['xl/workbook.xml'] as Uint8Array);
  const sheets: Sheet[] = [];
  let cellBudget = L.maxCells;
  const sheetNodes = [...wb.getElementsByTagName('sheet')];
  if (sheetNodes.length > L.maxSheets)
    throw new Error(`Çalışma kitabında ${sheetNodes.length} sayfa var; sınır ${L.maxSheets}.`);
  sheetNodes.forEach((sh, i) => {
    const name = sh.getAttribute('name') || `Sayfa${i + 1}`;
    const rid =
      sh.getAttribute('r:id') ??
      sh.getAttributeNS('http://schemas.openxmlformats.org/officeDocument/2006/relationships', 'id') ??
      '';
    const target = rels[rid] || `worksheets/sheet${i + 1}.xml`;
    const bytes = files[`xl/${target}`] ?? files[target];
    if (!bytes) return;
    const doc = xml(bytes);
    const aoa: string[][] = [];
    const rowNodes = [...doc.getElementsByTagName('row')];
    if (rowNodes.length > L.maxRows)
      throw new Error(`«${name}» sayfasında ${rowNodes.length} satır var; sınır ${L.maxRows}.`);
    for (const row of rowNodes) {
      const arr: string[] = [];
      for (const c of [...row.getElementsByTagName('c')]) {
        if (--cellBudget < 0) throw new Error(`Toplam hücre sayısı ${L.maxCells} sınırını aştı.`);
        const ref = c.getAttribute('r') ?? '';
        const idx = ref ? colIndex(ref) : arr.length;
        const t = c.getAttribute('t');
        let val = '';
        if (t === 'inlineStr')
          val = [...c.getElementsByTagName('t')].map((x) => x.textContent ?? '').join('');
        else {
          const v = c.getElementsByTagName('v')[0];
          const raw = v ? (v.textContent ?? '') : '';
          if (t === 's') val = shared[Number(raw)] ?? '';
          else if (t === 'd') val = raw.slice(0, 10);
          else if (raw !== '' && dstyles.has(Number(c.getAttribute('s') ?? -1)) && !Number.isNaN(Number(raw)))
            val = serialToISO(Number(raw));
          else val = raw;
        }
        if (idx >= L.maxCols)
          throw new Error(`«${name}» sayfasında sütun sayısı ${L.maxCols} sınırını aşıyor.`);
        const sv = String(val ?? '').trim();
        if (sv.length > L.maxCellChars)
          throw new Error(`Bir hücre ${L.maxCellChars} karakter sınırını aşıyor («${name}» sayfası).`);
        arr[idx] = sv;
      }
      for (let k = 0; k < arr.length; k++) if (arr[k] === undefined) arr[k] = '';
      aoa.push(arr);
    }
    const rows = aoa.filter((r) => r.some((x) => String(x).trim() !== ''));
    sheets.push({
      name,
      headers: (rows[0] ?? []).map((h) => String(h).trim()),
      rows: rows.slice(1),
    });
  });
  if (!sheets.length) throw new Error('Çalışma sayfası bulunamadı.');
  return { sheets, engine: 'builtin' };
}

export async function parseSpreadsheet(file: File): Promise<{ sheets: Sheet[]; engine: string }> {
  if (/\.csv$/i.test(file.name) || /csv|text/.test(file.type)) {
    const r = parseCSV(await file.text());
    return {
      sheets: [{ name: file.name, headers: r.headers, rows: r.rows }],
      engine: 'builtin-csv',
    };
  }
  if (/\.xls$/i.test(file.name)) throw new Error(XLS_LEGACY_MESSAGE);
  const head = new Uint8Array(await file.slice(0, 8).arrayBuffer());
  /* OLE2 (D0 CF 11 E0) is the old .xls container; a renamed file is rejected on its bytes. */
  if (head[0] === 0xd0 && head[1] === 0xcf && head[2] === 0x11 && head[3] === 0xe0)
    throw new Error(XLS_LEGACY_MESSAGE);
  return readXlsx(file);
}

// ------------------------------------------------------------------------ row validation (§v0.5)

export type Decision =
  | 'issue'
  | 'invalid'
  | 'skipped'
  | 'skipped_duplicate'
  | 'undecided'
  | 'undecided_overflow';

export interface ValidatedRow {
  n: number;
  values: Record<string, string>;
  email: string;
  lang: string;
  iss: string | null;
  exp: string | null;
  errors: string[];
  warnings: string[];
  dupe: DemoCredential | null;
  primary: string;
  fitOverflow: boolean;
  dupKey: string;
  skipped: boolean;
}

/** Everything `validateRows` needs from the flow, so the engine itself stays free of UI state. */
export interface ValidationInput {
  template: DemoTemplate;
  headers: string[];
  rows: string[][];
  mapping: Record<string, string>;
  fixes: Record<number, Record<string, string>>;
  skip: number[];
  lang: string;
}

const normKey = (s: unknown): string =>
  trLower(String(s ?? ''))
    .replace(/\s+/g, ' ')
    .trim();

/**
 * The single engine: the wizard, single issuance, CSV and XLSX all reach this function, so the
 * same row can never be accepted by one path and rejected by another.
 */
export function validateRows(db: DemoDatabase, input: ValidationInput): ValidatedRow[] {
  const t = input.template;
  const rel = importRelease(t);
  if (!rel) return [];
  const lay = rel as unknown as Layout;
  const hf = holderFieldKey(lay);
  const isOrg = rel.subjectType === 'organization';
  const flds = importFields(t);
  const idx: Record<string, number> = {};
  input.headers.forEach((h, i) => {
    const mapped = input.mapping[h];
    if (mapped) idx[mapped] = i;
  });
  const seenKeys = new Map<string, number>();
  const skipped = new Set(input.skip);
  const out: ValidatedRow[] = [];

  input.rows.forEach((r, i) => {
    const n = i + 2;
    const fix = input.fixes[n] ?? {};
    const get = (k: string): string => {
      if (fix[k] !== undefined) return String(fix[k]).trim();
      const col = idx[k];
      return col === undefined ? '' : String(r[col] ?? '').trim();
    };
    const errors: string[] = [];
    const warnings: string[] = [];
    const raw: Record<string, string> = {};
    for (const f of flds) if (f.kind === 'value') raw[f.key] = get(f.key);

    const email = get('email');
    let lang = (get('lang') || input.lang || rel.defaultLanguage || 'tr').toLowerCase();
    if (!['tr', 'en'].includes(lang)) {
      warnings.push(`Dil «${get('lang')}» tanınmadı, ${input.lang} kullanılacak`);
      lang = input.lang;
    }
    const iss = get('issue_date') ? normalizeDate(get('issue_date')) : todayIso();
    if (iss === 'invalid') errors.push('Veriliş tarihi okunamadı');
    const months = rel.validityPolicy ? rel.validityPolicy.months : null;
    const exp = get('expiry_date')
      ? normalizeDate(get('expiry_date'))
      : months && iss !== 'invalid' && iss
        ? addMonths(iss, months)
        : null;
    if (exp === 'invalid') errors.push('Geçerlilik tarihi okunamadı');
    if (iss && iss !== 'invalid' && exp && exp !== 'invalid' && exp <= iss)
      errors.push('Geçerlilik tarihi veriliş tarihinden önce');

    /* The period is derived BEFORE validation so the batch path and the single-record path agree. */
    if (
      isOrg &&
      !raw.certification_period &&
      iss &&
      iss !== 'invalid' &&
      rel.fieldDefinitions.some((f) => f.key === 'certification_period')
    ) {
      raw.certification_period = `${fmtDate(iss, lang as 'tr' | 'en', 'month')} – ${
        exp && exp !== 'invalid' ? fmtDate(exp, lang as 'tr' | 'en', 'month') : '…'
      }`;
    }

    const vr = validateClaims(rel.fieldDefinitions, raw);
    const labelOf = (k: string): string => {
      const d = rel.fieldDefinitions.find((f) => f.key === k);
      return d ? fieldLabelOf(d) : k;
    };
    for (const e of vr.errors) errors.push(`${labelOf(e.key ?? '')}: ${e.message}`);
    const values = vr.values;
    const primary = values[hf] ?? '';
    if (!primary && !errors.some((x) => x.startsWith(labelOf(hf))))
      errors.push(`${importFieldLabel(t, hf)} boş`);
    if (!email) errors.push('E-posta boş');
    else if (!EMAIL_RE.test(email)) errors.push('E-posta geçersiz');

    /* A duplicate inside the file and a duplicate against the ledger share one canonical key. */
    const key = duplicateKey(rel, values, email);
    if (primary && email) {
      const hit = seenKeys.get(key);
      if (hit !== undefined) errors.push(`Dosya içinde mükerrer (satır ${hit})`);
      else seenKeys.set(key, n);
    }

    let dupe: DemoCredential | null = null;
    if (!errors.length) {
      dupe = findDuplicate(db, rel, values, email);
      if (dupe)
        warnings.push(
          `Aynı sahip için ${STATUS_TR[credentialViewState(dupe)] || credentialViewState(dupe)} kayıt var (${shortCode(dupe.publicCode)})`,
        );
      else if (!isOrg) {
        const sameMail = db.credentials.find(
          (c) =>
            c.templateId === t.id &&
            c.state !== 'revoked' &&
            normKey(c.privateContactEmail) === normKey(email),
        );
        if (sameMail) warnings.push(`Aynı e-posta farklı adla kayıtlı: ${displayName(sameMail)}`);
      }
    }

    let fitOverflow = false;
    if (!errors.length) {
      const plan = resolveRenderPlan(lay, { ...values } as RenderData, {
        width: canvasW(lay.canvas),
        height: canvasH(lay.canvas),
      });
      const pr = plan.items.find((x) => x.kind === 'text' && x.layer.fieldKey === hf);
      if (pr?.overflow) {
        fitOverflow = true;
        warnings.push(
          'Ad minimum puntoda bile sığmıyor — satırı düzeltin, atlayın ya da iki satırlı render ile devam edin',
        );
      }
    }

    out.push({
      n,
      values,
      email,
      lang,
      iss: iss === 'invalid' ? null : iss,
      exp: exp === 'invalid' ? null : exp,
      errors,
      warnings,
      dupe,
      primary,
      fitOverflow,
      dupKey: key,
      skipped: skipped.has(n),
    });
  });
  return out;
}

export interface DecisionContext {
  dupePolicy: string;
  rowChoice: Record<number, string>;
  overflowChoice: Record<number, string>;
}

export function decisionFor(r: ValidatedRow, ctx: DecisionContext): Decision {
  if (r.errors.length) return 'invalid';
  if (r.skipped) return 'skipped';
  if (r.fitOverflow) {
    const ch = ctx.overflowChoice[r.n];
    if (ch === 'twoLines') return 'issue';
    if (ch === 'skip') return 'skipped';
    return 'undecided_overflow';
  }
  if (r.dupe) {
    if (ctx.dupePolicy === 'skip') return 'skipped_duplicate';
    if (ctx.dupePolicy === 'issue') return 'issue';
    return ctx.rowChoice[r.n] === 'issue'
      ? 'issue'
      : ctx.rowChoice[r.n] === 'skip'
        ? 'skipped_duplicate'
        : 'undecided';
  }
  return 'issue';
}

// ---------------------------------------------------------- preview record and e-mail (`m03`)

const autoFieldValue = (
  key: string,
  ctx: {
    lang: 'tr' | 'en';
    title: string;
    issueDate: string | null;
    expiresAt: string | null;
    publicCode: string;
    credentialUri: string;
  },
): string => {
  if (key === 'credential_title') return ctx.title;
  if (key === 'issue_date') return fmtDate(ctx.issueDate, ctx.lang);
  if (key === 'expiry_date')
    return ctx.expiresAt ? fmtDate(ctx.expiresAt, ctx.lang) : ctx.lang === 'en' ? 'No expiration' : 'Süresiz';
  if (key === 'credential_code') return ctx.publicCode;
  if (key === 'credential_url' || key === 'verification_qr') return ctx.credentialUri;
  return '';
};

export interface RenderDataContext {
  lang: 'tr' | 'en';
  title: string;
  issueDate: string | null;
  expiresAt: string | null;
  publicCode: string;
  credentialUri: string;
  claims: Record<string, string>;
}

/** Private and `public_metadata` fields never reach the render data — only renderable ones do. */
export function buildRenderData(release: DemoRelease, ctx: RenderDataContext): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of release.fieldDefinitions ?? []) {
    if (f.auto) {
      out[f.key] = autoFieldValue(f.key, ctx);
      continue;
    }
    if (!canRender(f.visibility)) continue;
    out[f.key] = ctx.claims[f.key] ?? String(f.defaultValue ?? '');
  }
  out.verification_url = ctx.credentialUri;
  if ((release.fieldDefinitions ?? []).some((f) => f.key === 'credential_code'))
    out.credential_code = ctx.publicCode;
  return out;
}

const fillPlaceholders = (tpl: unknown, vars: Record<string, string>): string =>
  String(tpl ?? '').replace(/\{\{(\w+)\}\}/g, (_, k: string) => vars[k] ?? '');

export interface RenderedEmail {
  subject: string;
  body: string;
  cta: string;
  ctaHash: string;
}

/**
 * `renderEmail`, for a record that has not been written anywhere. The CTA is the claim link and
 * never the public URL (§6.4): a pending record's public page shows no personal data (rule 12),
 * so sending the public address instead would be sending the recipient somewhere empty.
 */
export function renderPreviewEmail(
  release: DemoRelease,
  placeholders: Record<string, string>,
  claimCta: string,
): RenderedEmail {
  const ed = (release.emailDefaults ?? {}) as {
    subjectTemplate?: string;
    bodyTemplate?: string;
  };
  return {
    subject: fillPlaceholders(ed.subjectTemplate, placeholders),
    body: fillPlaceholders(ed.bodyTemplate, placeholders),
    cta: claimCta,
    /* API GAP: the raw claim token exists only inside issuance; a preview has no link to offer. */
    ctaHash: '#',
  };
}
